// src/pages/Realisations.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { fadeIn, scaleIn } from '../styles/animations';
import { realizationsService, categoriesService } from '../services/api';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';

// Fallback category list used only if the API call fails
const FALLBACK_CATEGORIES = [
    { id: 'all', name: 'Toutes les réalisations', slug: 'all' },
    { id: 'terrasses-en-bois', name: 'Terrasses en bois', slug: 'terrasses-en-bois' },
    { id: 'terrasses-en-bois-composite', name: 'Terrasses composite', slug: 'terrasses-en-bois-composite' },
    { id: 'plages-de-piscine-en-bois', name: 'Plages de piscine', slug: 'plages-de-piscine-en-bois' },
    { id: 'balcons-terrasses-appartement', name: 'Balcons & appartements', slug: 'balcons-terrasses-appartement' },
    { id: 'amenagements-exterieurs', name: 'Aménagements extérieurs', slug: 'amenagements-exterieurs' },
    { id: 'terrasse-en-ceramique-sur-plots-ou-structure-metallique', name: 'Céramique', slug: 'terrasse-en-ceramique-sur-plots-ou-structure-metallique' },
];

const RealisationsPageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const HeroSection = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.light};
  text-align: center;
`;

const HeroContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  color: ${props => props.theme.colors.light};
`;

const RealisationsContent = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.gray};
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid #eee;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const FilterTitle = styled.h3`
  margin: 0;
  margin-right: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.light};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.textMain};
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  font-size: ${props => props.theme.fontSizes.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : '#f5f5f5'};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .project-image img {
      transform: scale(1.05);
    }
  }
`;

const ProjectImage = styled.div`
  height: 240px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const ProjectContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
`;

const ProjectLocation = styled.span`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: ${props => props.theme.spacing.xl} auto 0;
  padding: 0.75rem 1.5rem;
  background-color: transparent;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.light};
  }
  
  &:disabled {
    background-color: #f0f0f0;
    color: #999;
    border-color: #ddd;
    cursor: not-allowed;
  }
`;

const Realisations = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [projects, setProjects] = useState([]);
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [displayCount, setDisplayCount] = useState(6);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        // Load categories from API
        // categoriesService.getAll() returns a direct array
        categoriesService.getAll()
            .then(data => {
                const list = Array.isArray(data) ? data : [];
                if (list.length > 0) {
                    setCategories([
                        { id: 'all', name: 'Toutes les réalisations', slug: 'all' },
                        ...list
                    ]);
                }
            })
            .catch(() => {
                // Keep FALLBACK_CATEGORIES on error
            });

        // Load realizations from API
        // realizationsService.getAll() returns { realizations: [...], pagination: {...} }
        const fetchProjects = async () => {
            try {
                setLoading(true);

                const response = await realizationsService.getAll();
                // response is { realizations: [...], pagination: {...} }
                const raw = response.realizations || [];
                setProjects(Array.isArray(raw) ? raw : []);
                setLoading(false);
            } catch (error) {
                console.error('Erreur lors du chargement des réalisations:', error);
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    // Filtre les projets en fonction de la catégorie sélectionnée
    useEffect(() => {
        const filtered = selectedCategory === 'all'
            ? projects
            : projects.filter(project => project.category_slug === selectedCategory);

        setFilteredProjects(filtered);
        setDisplayCount(6); // Réinitialise le nombre affiché lors du changement de filtre
        setHasMore(filtered.length > 6);
    }, [selectedCategory, projects]);

    // Fonction pour charger plus de projets
    const loadMore = () => {
        const newDisplayCount = displayCount + 6;
        setDisplayCount(newDisplayCount);
        setHasMore(filteredProjects.length > newDisplayCount);
    };

    return (
        <RealisationsPageWrapper>
            <Helmet>
                <title>Nos Réalisations - Decko | Créateur de vos parquets de jardin</title>
                <meta name="description" content="Découvrez nos réalisations de terrasses en bois, parquets extérieurs et aménagements sur mesure pour espaces de vie extérieurs." />
            </Helmet>

            <HeroSection>
                <HeroContainer>
                    <HeroTitle>Nos Réalisations</HeroTitle>
                    <HeroSubtitle>Découvrez nos créations personnalisées</HeroSubtitle>
                </HeroContainer>
            </HeroSection>

            <RealisationsContent>
                <ContentContainer>
                    <FilterBar>
                        <FilterTitle>Filtrer par :</FilterTitle>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {categories.map(category => (
                                <FilterButton
                                    key={category.id}
                                    active={selectedCategory === String(category.slug || category.id)}
                                    onClick={() => setSelectedCategory(String(category.slug || category.id))}
                                >
                                    {category.name}
                                </FilterButton>
                            ))}
                        </div>
                    </FilterBar>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                            <p>Chargement des réalisations...</p>
                        </div>
                    ) : filteredProjects.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                            <h3>Aucune réalisation trouvée</h3>
                            <p>Aucune réalisation ne correspond à la catégorie sélectionnée.</p>
                            <Button
                                variant="secondary"
                                onClick={() => setSelectedCategory('all')}
                            >
                                Voir toutes les réalisations
                            </Button>
                        </div>
                    ) : (
                        <>
                            <ProjectsGrid>
                                {filteredProjects.slice(0, displayCount).map((project, index) => (
                                    <ProjectCard key={project.id} delay={`${0.1 * index}s`}>
                                        <ProjectImage className="project-image">
                                            <img src={project.featured_image ? `https://decko.fr/uploads/${project.featured_image}` : ''} alt={project.title} />
                                        </ProjectImage>
                                        <ProjectContent>
                                            <ProjectHeader>
                                                <ProjectTitle>{project.title}</ProjectTitle>
                                                <ProjectLocation>{project.location}</ProjectLocation>
                                            </ProjectHeader>
                                            <ProjectDescription>{project.description}</ProjectDescription>
                                            <Button
                                                variant="text"
                                                to={`/realisations/${project.slug || project.id}`}
                                            >
                                                Voir le projet
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                    <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                                </svg>
                                            </Button>
                                        </ProjectContent>
                                    </ProjectCard>
                                ))}
                            </ProjectsGrid>

                            {hasMore && (
                                <LoadMoreButton onClick={loadMore}>
                                    Charger plus de réalisations
                                </LoadMoreButton>
                            )}
                        </>
                    )}
                </ContentContainer>
            </RealisationsContent>
        </RealisationsPageWrapper>
    );
};

export default Realisations;